import React, { useState } from 'react'

import { TextClamp } from '../../components/text/TextClamp'
import { ChartLabelFullText, ChartLabelRenderMode } from './ChartLabelFullText'

/**
 * The one truncation rule for a chart label (plots plan LB, owner decision 24).
 *
 * A chart prints the whole source text. When the text does not fit the lines
 * its box allows, the clamp cuts it with an ellipsis; on an interactive
 * surface the shared (i) beside it (`ChartLabelFullText`) shows the whole value
 * on hover and focus. A static render (the PDF) keeps the ellipsis and draws no
 * (i): its markup is the plain clamp, with the whole value as the native title.
 *
 * Renders the clamp and, when needed, the (i) as its next sibling, so the
 * caller places both in a row. The clamp is never re-parented when the (i)
 * appears (a remount would reset its truncation state).
 */

export interface ChartLabelProps {
  /** The text the chart prints: the source text. */
  text: string
  /** The source text when the chart prints another label (`fullLabel`). */
  fullText?: string | null
  /** Position among the plot's hints: `chart-label-full-text-{hintIndex}`. */
  hintIndex: number
  maxLines: number
  minLines?: number
  lineHeight?: number | string
  style?: React.CSSProperties
  className?: string
  as?: keyof JSX.IntrinsicElements
  renderMode?: ChartLabelRenderMode
  /** Pixel size of the (i). */
  hintSize?: number
  /** Native title; defaults to the whole value. Only a static render carries it. */
  title?: string
  /** A note shown in the (i) tooltip (focusable), under the whole value if any. */
  description?: string | null
  /** The (i)'s testid when it is not one of a plot's indexed hints. */
  hintTestId?: string
  'data-testid'?: string
}

export function ChartLabel(props: ChartLabelProps) {
  const {
    text,
    fullText,
    hintIndex,
    maxLines,
    minLines = 1,
    lineHeight = '1.2',
    style,
    className,
    as,
    renderMode = 'interactive',
    hintSize,
    title,
    description,
    hintTestId,
  } = props

  const [isTruncated, setIsTruncated] = useState(false)

  const isStatic = renderMode === 'static'

  return (
    <>
      <TextClamp
        as={as}
        className={className}
        style={style}
        minLines={minLines}
        maxLines={maxLines}
        lineHeight={lineHeight}
        renderMode={renderMode}
        title={isStatic ? (title ?? fullText ?? text) : undefined}
        onTruncatedChange={setIsTruncated}
        data-testid={props['data-testid']}
      >
        {text}
      </TextClamp>
      <ChartLabelFullText
        label={text}
        fullText={fullText}
        truncated={isTruncated}
        index={hintIndex}
        renderMode={renderMode}
        size={hintSize}
        description={description}
        testId={hintTestId}
      />
    </>
  )
}
